import type { ReactNode } from 'react';
import type { PostureStatus } from '@/components/ui/posture-chip';
import { useCountUp, useEntryMotion } from '@/components/ui/charts';
import { cn } from '@/lib/utils/cn';

/**
 * The Overview's frames (prototype `.card`, `.card-title-row`, `.vcard`,
 * `.vlabel`, `.vbig`, `.vsub`, `.p-label`), in the theme's tokens.
 */

/** A titled card: the h3 on the left, a meta line or a link on the right. */
export function OverviewCard({
  title,
  aside,
  children,
  className,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      aria-label={title}
      className={cn('min-w-0 rounded-card border border-ink-100 bg-paper-0 p-card-pad', className)}
    >
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-h3 font-semibold text-ink-900">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** The meta text a card's title row carries on its right. */
export function CardMeta({ children }: { children: ReactNode }) {
  return <span className="text-meta text-ink-500">{children}</span>;
}

/** The small grey link in a card ("All findings →"). */
export const CARD_LINK =
  'text-meta text-ink-500 no-underline hover:text-ink-900 hover:underline hover:underline-offset-[3px]';

const TOP_RULE: Record<PostureStatus, string> = {
  critical: 'bg-posture-critical',
  'at-risk': 'bg-posture-at-risk',
  healthy: 'bg-posture-healthy',
  unassessed: 'bg-ink-100',
};

/** A verdict card: a 3px rule on top in the verdict's colour, an overline label. */
export function VerdictCard({
  label,
  band,
  children,
}: {
  label: string;
  band: PostureStatus;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={label}
      data-band={band}
      className="relative flex min-w-0 flex-col gap-2.5 overflow-hidden rounded-card border border-ink-100 bg-paper-0 px-5 py-[18px]"
    >
      <span aria-hidden="true" className={cn('absolute inset-x-0 top-0 h-[3px]', TOP_RULE[band])} />
      <div className="text-[11px] font-semibold uppercase leading-4 tracking-[0.08em] text-ink-500">
        {label}
      </div>
      {children}
    </section>
  );
}

/** The verdict's big line: 34px, tight tracking. */
export const BIG =
  'flex items-center gap-2.5 text-[34px] font-semibold leading-10 tracking-[-0.015em] text-ink-900';

/** The 13px line under it. */
export const SUB = 'text-body-sm text-ink-700';

/** The uppercase overline inside a card ("Largest gaps", "By domain"). */
export const OVERLINE =
  'text-[11px] font-semibold uppercase leading-4 tracking-[0.06em] text-ink-500';

/** A figure that counts up from 0 on entry (none under reduced motion). */
export function CountUp({ value, motionKey }: { value: number; motionKey?: string }) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const shown = useCountUp(value, 0, animate, runKey);
  return <>{shown ?? Math.round(value)}</>;
}
